import React, { FC, Suspense, useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { T } from 'src/shared/helpers';
import { ZSwitch } from 'src/components/widgets/zswitch';
import type { QuoteRuleEditorProps } from '../common/types';
import { useToolDefinitions } from '../common/constants';
import type { quoteRuleEditor_QuoteRuleDetailQuery } from './__generated__/quoteRuleEditor_QuoteRuleDetailQuery.graphql';
import type { quoteRuleEditor_UpdateQuoteRuleMutation } from './__generated__/quoteRuleEditor_UpdateQuoteRuleMutation.graphql';
import {
    StackedBreadcrumb,
    StackedBody,
    SectionCard,
    SectionCardTitle,
    FormGroup,
    FormLabel,
    FormInput,
    FormSwitchRow,
    FormSwitchLabel,
    SharedWarningCallout
} from '../common/styled';

const quoteRuleDetailQuery = graphql`
    query quoteRuleEditor_QuoteRuleDetailQuery($id: String!) {
        quoteRule(id: $id) {
            id
            quoteRuleID
            name
            allowAddToCart
            notifyEmailList
            isValidForAllProducts
            formControlsData
            products
        }
    }
`;

const QuoteRuleEditorContent: FC<QuoteRuleEditorProps> = ({ itemId, productId, toolKey, onBack, onSave, saveRef }) => {
    const TOOL_DEFINITIONS = useToolDefinitions();
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query quoteRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const json = useLazyLoadQuery<quoteRuleEditor_QuoteRuleDetailQuery>(quoteRuleDetailQuery, { id: itemId });
    const rule = json?.quoteRule;
    const ruleName = rule?.name || '';
    const isShared = !rule?.isValidForAllProducts && (rule?.products?.length ?? 0) > 1;

    const [name, setName] = useState(() => rule?.name || '');
    const [allowAddToCart, setAllowAddToCart] = useState(() => rule?.allowAddToCart ?? false);
    const [notifyEmailList, setNotifyEmailList] = useState(() => rule?.notifyEmailList || '');

    const [commitUpdateQuoteRule] = useMutation<quoteRuleEditor_UpdateQuoteRuleMutation>(graphql`
        mutation quoteRuleEditor_UpdateQuoteRuleMutation($input: QuoteRuleInput!) {
            quoteRuleUpdate(input: $input) {
                id
                quoteRuleID
                name
                allowAddToCart
                notifyEmailList
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!rule) return;
        try {
            commitUpdateQuoteRule({
                variables: {
                    input: {
                        quoteRuleID: rule.quoteRuleID,
                        name: name,
                        allowAddToCart: allowAddToCart,
                        notifyEmailList: notifyEmailList,
                        isValidForAllProducts: rule.isValidForAllProducts,
                        formControlsData: rule.formControlsData,
                        products: rule.products
                    }
                },
                onCompleted: () => onSave(),
                onError: () => {
                    /* ignore */
                }
            });
        } catch {
            /* ignore */
        }
    }, [rule, name, allowAddToCart, notifyEmailList, onSave, commitUpdateQuoteRule]);

    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName} / {ruleName}
            </StackedBreadcrumb>
            <StackedBody>
                {isShared && (
                    <SharedWarningCallout>
                        {T._('This rule is assigned to', 'Admin')} <strong>{rule?.products?.length}</strong>{' '}
                        {T._('products. Any changes will apply to all of them.', 'Admin')}
                    </SharedWarningCallout>
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Quote Rule Settings', 'Admin')}</SectionCardTitle>

                    <FormGroup>
                        <FormLabel>{T._('Rule name', 'Admin')}</FormLabel>
                        <FormInput type='text' value={name} onChange={(e) => setName(e.target.value)} />
                    </FormGroup>

                    <FormSwitchRow>
                        <FormSwitchLabel>{T._('Allow adding to cart alongside quote request', 'Admin')}</FormSwitchLabel>
                        <ZSwitch checked={allowAddToCart} onChange={() => setAllowAddToCart(!allowAddToCart)} />
                    </FormSwitchRow>

                    <FormGroup>
                        <FormLabel>{T._('Notification emails', 'Admin')}</FormLabel>
                        <FormInput
                            type='text'
                            value={notifyEmailList}
                            onChange={(e) => setNotifyEmailList(e.target.value)}
                            placeholder='email1@example.com;email2@example.com'
                        />
                    </FormGroup>
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const QuoteRuleEditor: FC<QuoteRuleEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <ContentLoader
                            speed={2}
                            width='100%'
                            height='60'
                            backgroundColor={theme.skeleton.backgroundColor}
                            foregroundColor={theme.skeleton.foregroundColor}
                        >
                            <rect x='0' y='0' rx='8' ry='8' width='100%' height='16' />
                            <rect x='0' y='24' rx='8' ry='8' width='80%' height='16' />
                            <rect x='0' y='48' rx='8' ry='8' width='60%' height='16' />
                        </ContentLoader>
                    </StackedBody>
                </>
            }
        >
            <QuoteRuleEditorContent {...props} />
        </Suspense>
    );
};
